import React, {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  TableCell,
  TextField
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { withStyles } from '@mui/styles';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { useForm } from 'react-hook-form';
import { compareObjects } from 'src/utils/validate';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import {
  getTimeEntryDetailsReport,
  getTimeEntryTaskBasedDetailsReport
} from 'src/services/timeManagementService';
import NoRecord from '../common/NoRecord';
import moment from 'moment';
import { useAppSelector } from 'src/hooks/hooks';
import fileDownload from 'js-file-download';
import ClientDropdown from '../common/ClientDropdown';
import ProjectDropdown from '../common/ProjectDropdown';
import ResourceDropdown from '../common/ResourceDropdown';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { TTaskBasedReportDetails } from '../../types/time-entry-report.type';
import { ITaskBasedDetailsReportFilter } from '../../interface/time-entry-report.interface';
import ExcelJS from 'exceljs';
import { ETaskStatus } from 'src/content/timeEntry/enums/task-status.enum';
import ProjectDropdownFilter from '../common/ProjectDropdownFilter';
import ResourceDropdownFilter from '../common/ResourceDropdownFilter';
interface TaskBasesReportTableProps {
  permissions: any;
}

export interface TaskBasedReportTableRef {
  handleDownload: () => void;
}

const TaskBasesReportTable = forwardRef<
  TaskBasedReportTableRef,
  TaskBasesReportTableProps
>(({ permissions }, ref) => {
  const pagination_wrapper_ref = useRef(null);
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const dispatch = useDispatch();
  const myRef = useRef<DateRangePicker>();

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  let initialFilter = {
    clientId: null,
    projectId: null,
    employeeId: null,
    datePeriod: {
      fromDate: moment(new Date()).format('YYYY-MM-DD'),
      toDate: moment(new Date()).format('YYYY-MM-DD')
    },
    isSubmitted: true,
    limit: 10,
    offset: 1
  } as unknown as ITaskBasedDetailsReportFilter;

  const [filter, setFilter] = useState(
    initialFilter as ITaskBasedDetailsReportFilter
  );
  const [prevFilter, setPrevFilter] = useState(
    initialFilter as ITaskBasedDetailsReportFilter
  );

  const [toggleFilter, setToggleFilter] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(true);
  const [isClearDisabled, setIsClearDisabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [filterPayload, setFilterPayload] = useState({
    clientId: null,
    projectId: null,
    employeeId: null,
    datePeriod: null
  });

  const [data, setData] = useState<TTaskBasedReportDetails[]>([]);

  const [total, setTotal] = useState<number>(0);

  const { register, handleSubmit, reset, setValue } =
    useForm<ITaskBasedDetailsReportFilter>({
      defaultValues: initialFilter
    });

  const handlePageChange = (event: any, newPage: number): void => {
    setFilter({
      ...prevFilter,
      offset: newPage == 0 ? 1 : newPage + 1,
      limit: filter.limit,
      isSubmitted: true
    });
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFilter({
      ...prevFilter,
      limit: parseInt(event.target.value),
      isSubmitted: true
    });
  };

  const onSubmit = () => {
    const newFilter = {
      ...filter,
      isSubmitted: true,
      offset: 1,
      limit: 10
    };

    if (isFilterChanged) {
      setFilter(newFilter);
      setPrevFilter(newFilter);
      setIsFilterChanged(false);
    }
  };

  //clear the filter
  const resetFilterResult = () => {
    reset();

    //clear date picker
    const today = new Date();
    myRef.current?.setStartDate(today);
    myRef.current?.setEndDate(today);

    const result = compareObjects(initialFilter, filter, 'isSubmitted');
    !result &&
      setFilter({
        ...initialFilter,
        isSubmitted: true
      });

    setPrevFilter({
      ...initialFilter,
      isSubmitted: true
    });
  };

  useEffect(() => {
    const newFilter = {
      clientId: filter.clientId,
      datePeriod: {
        fromDate: filter.datePeriod.fromDate,
        toDate: filter.datePeriod.toDate
      },
      projectId: filter.projectId,
      employeeId: filter.employeeId
    };

    const isNullish = Object.values(newFilter).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(newFilter).length === 0 || isNullish) {
      setIsDisabled(true);
      setIsClearDisabled(true);
    } else {
      setIsDisabled(false);
      setIsClearDisabled(false);
    }
  }, [filter]);

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);

  const getTimeEntryReportDetails = () => {
    dispatch(AppActions.showLoader());

    const payload = {
      clientId: filter.clientId,
      projectId: filter.projectId,
      employeeId: filter.employeeId,
      datePeriod: filter.datePeriod
    };

    setFilterPayload(payload);

    getTimeEntryTaskBasedDetailsReport(payload, filter.offset, filter.limit)
      .then((res) => {
        const data = res?.data?.data;
        const transformedData = tranceFormData(
          data?.timeEntryDetailsReportDtoList
        );
        setData(transformedData);
        setTotal(data?.totalCount);
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
      });
  };

  const tranceFormData = (response: TTaskBasedReportDetails[]) => {
    if (response?.length > 0) {
      const resData = response.map((item: TTaskBasedReportDetails) => {
        return {
          ...item,
          hours: formatTime(item.hours)
        };
      });
      return resData;
    }
    return [];
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString
      .replace('h', '')
      .replace('m', '')
      .trim()
      .split(' ');

    const formattedHours = hours.padStart(2, '0');
    const formattedMinutes = minutes.padStart(2, '0');

    return `${formattedHours} : ${formattedMinutes}`;
  };

  //call API for load first time and when click filter button
  useEffect(() => {
    if (filter.isSubmitted) {
      getTimeEntryReportDetails();
      setFilter((prev) => ({
        ...prev,
        isSubmitted: false
      }));
    }
  }, [filter]);

  const handleDownload = () => {
    dispatch(AppActions.showLoader());

    getTimeEntryTaskBasedDetailsReport(filterPayload)
      .then((res) => {
        const data = res?.data?.data;
        const transformedData = tranceFormData(
          data?.timeEntryDetailsReportDtoList
        );
        if (transformedData?.length > 0) {
          downloadExcelSheet(transformedData);
        } else {
          setSnackBarMessage('No data in selected date range.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        }
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
      });
  };

  useImperativeHandle(ref, () => ({
    handleDownload
  }));

  const downloadExcelSheet = (downloadData: TTaskBasedReportDetails[]) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Summary');

    worksheet.columns = [
      { header: 'Client', key: 'clientName', width: 15 },
      { header: 'Project', key: 'projectName', width: 15 },
      { header: 'Resource Name', key: 'employeeName', width: 15 },
      { header: 'Activity Type', key: 'taskType', width: 10 },
      { header: 'Activity Id', key: 'activityId', width: 10 },
      { header: 'Reference Id', key: 'referenceId', width: 10 },
      { header: 'Task Id', key: 'taskAssignId', width: 10 },
      { header: 'Task Name', key: 'taskName', width: 15 },
      { header: 'Task Status', key: 'assignmentStatus', width: 20 },
      { header: 'Planned Duration (hh:mm)', key: 'planHours', width: 10 },
      { header: 'Duration (hh:mm)', key: 'hours', width: 10 }
    ];

    worksheet.getRow(1).eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3498DB' }
      };
      cell.font = {
        color: { argb: 'FFFFFF' },
        bold: true
      };
    });

    downloadData.forEach((item) => {
      worksheet.addRow({
        clientName: item.clientName,
        projectName: item.projectName,
        employeeName: item.employeeName,
        taskType: item.taskType,
        activityId: item.taskId,
        referenceId: item.referenceId,
        taskAssignId: item.taskAssignId,
        taskName: item.taskName,
        assignmentStatus: item.assignmentStatus,
        planHours: item.planHours,
        hours: item.hours
      });
    });

    const currentDatetime = moment().format('YYYYMMDDHHmm');

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `TimeEntry_Task_Based_Detailed_Report_${currentDatetime}.xlsx`;
      link.click();
    });
  };

  const convertQuarterText = (quarter: number) => {
    let quarterText;

    if (quarter === 1) {
      quarterText = '1st Quarter';
    } else if (quarter === 2) {
      quarterText = '2nd Quarter';
    } else if (quarter === 3) {
      quarterText = '3rd Quarter';
    } else {
      quarterText = '4th Quarter';
    }
    return quarterText;
  };

  const conventYearText = (date: string) => {
    return new Date(date).getFullYear();
  };

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  const getMonthShortName = (monthNumber: number) => {
    return monthNames[monthNumber - 1];
  };

  // custom table cloumn cell
  const CustomTableCloumnCell = ({ content }) => {
    return (
      <TableCell
        style={{
          background: 'white',
          whiteSpace: 'nowrap',
          alignItems: 'center',
          color: '#232C68',
          fontSize: '11px'
        }}
      >
        <Typography
          paddingBottom="0.25rem"
          paddingTop="0.5rem"
          variant="body2"
          style={{ fontSize: '11px' }}
          gutterBottom
          noWrap
        >
          {content}
        </Typography>
      </TableCell>
    );
  };

  const generateStatus = (status: string) => {
    let tStatus = '';
    if (status === ETaskStatus.TO_DO) {
      return (tStatus = 'To Do');
    } else if (status === ETaskStatus.IN_PROGRESS) {
      return (tStatus = 'In Progress');
    } else if (status === ETaskStatus.ON_HOLD) {
      return (tStatus = 'On Hold');
    } else if (status === ETaskStatus.COMPLETED) {
      return (tStatus = 'Completed');
    }
    return tStatus;
  };

  const getInitSettings = () => {
    const today = new Date();
    let settings = {
      startDate: today,
      endDate: today
    };
    return settings;
  };

  return (
    <div className="time-sheet-report-table">
      <Card>
        <Grid
          sx={{ px: 2, py: 2 }}
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            id="btn-setToggleFilter"
            sx={{
              ml: 1,
              mt: { xs: 2, md: 0 },
              color: `${!toggleFilter && 'gray'}`
            }}
            variant="outlined"
            startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
            onClick={() => {
              setToggleFilter(!toggleFilter);
            }}
            style={{ height: '1.75rem', fontSize: '12px' }}
          >
            Filters
          </Button>
          <Grid style={{ fontSize: '12px' }}>
            Data display is for the duration :{' '}
            {moment(filter?.datePeriod?.fromDate).format('YYYY MMM DD') +
              ' - ' +
              moment(filter?.datePeriod?.toDate).format('YYYY MMM DD')}{' '}
          </Grid>
        </Grid>
        {toggleFilter && (
          <Grid px={2} pb={2} style={{ margin: 0 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'between',
                  flexWrap: 'nowrap',
                  alignItems: 'center'
                }}
              >
                <Grid item xs={3} width="20%">
                  <ClientDropdown
                    title="Client"
                    {...register('clientId')}
                    value={filter?.clientId || null}
                    onChange={(selected: string) => {
                      setValue('employeeId', null);
                      setFilter((prev) => ({
                        ...prev,
                        clientId: selected !== '' ? selected : null
                      }));
                      setValue('clientId', selected);
                      setIsFilterChanged(true);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <ProjectDropdownFilter
                    title="Project"
                    value={filter?.projectId || ''}
                    customerIds={
                      filter?.clientId ? filter.clientId.split(',') : []
                    }
                    onChange={(selected) => {
                      const selectedArray = selected.split(',').filter(Boolean);
                      setFilter((prev) => ({
                        ...prev,
                        projectId:
                          selectedArray.length > 0
                            ? selectedArray.join(',')
                            : null
                      }));
                      setValue('projectId', selected);
                      setIsFilterChanged(true);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <ResourceDropdownFilter
                    title="Resource Name"
                    {...register('employeeId')}
                    value={filter?.employeeId || ''}
                    projectIds={
                      filter?.projectId ? filter.projectId.split(',') : []
                    }
                    customerIds={
                      filter?.clientId ? filter.clientId.split(',') : []
                    }
                    startDate={
                      filter?.datePeriod?.fromDate
                        ? moment(filter.datePeriod.fromDate).format(
                            'YYYY-MM-DD'
                          )
                        : null
                    }
                    endDate={
                      filter?.datePeriod?.toDate
                        ? moment(filter.datePeriod.toDate).format('YYYY-MM-DD')
                        : null
                    }
                    onChange={(selected: string) => {
                      setFilter((prev) => ({
                        ...prev,
                        employeeId: selected
                      }));
                      setValue('employeeId', selected);
                      setIsFilterChanged(true);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <div className="date-range-picker">
                    <DateRangePicker
                      ref={myRef}
                      initialSettings={getInitSettings()}
                      onApply={(event, picker) => {
                        setFilter((prev) => ({
                          ...prev,
                          datePeriod: {
                            fromDate: moment(picker.startDate).format(
                              'YYYY-MM-DD'
                            ),
                            toDate: moment(picker.endDate).format('YYYY-MM-DD')
                          }
                        }));
                        myRef.current?.setStartDate(picker.startDate);
                        myRef.current?.setEndDate(picker.endDate);

                        setValue(
                          'datePeriod.fromDate',
                          moment(picker.startDate).format('YYYY-MM-DD')
                        );
                        setValue(
                          'datePeriod.toDate',
                          moment(picker.endDate).format('YYYY-MM-DD')
                        );
                        setIsFilterChanged(true);
                      }}
                      onCancel={() => {
                        const today = new Date();
                        myRef.current?.setStartDate(today);
                        myRef.current?.setEndDate(today);

                        setFilter((prev) => ({
                          ...prev,
                          datePeriod: {
                            fromDate: moment(today).format('YYYY-MM-DD'),
                            toDate: moment(today).format('YYYY-MM-DD')
                          }
                        }));
                        setValue(
                          'datePeriod.fromDate',
                          moment(today).format('YYYY-MM-DD')
                        );
                        setValue(
                          'datePeriod.toDate',
                          moment(today).format('YYYY-MM-DD')
                        );
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <TextField
                          placeholder="YYYY-MM-DD - YYYY-MM-DD"
                          value={
                            filter?.datePeriod?.fromDate +
                            ' - ' +
                            filter?.datePeriod?.toDate
                          }
                          id="date"
                          fullWidth
                          size="small"
                          style={{ paddingTop: 8, fontSize: '12px' }}
                        />
                        <CalendarTodayIcon
                          className="calendar-icon"
                          fontSize={'small'}
                        />
                      </div>
                    </DateRangePicker>
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'between',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  marginTop: '3px'
                }}
              >
                <Grid
                  item
                  xs={3}
                  width="60%"
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginLeft: 'auto',
                    marginTop: '8px',
                    display: 'flex'
                  }}
                >
                  <Button
                    sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                    variant="outlined"
                    onClick={resetFilterResult}
                    disabled={isClearDisabled}
                    className={isClearDisabled && 'clear-btn'}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                    id="btn-resetFilterResult"
                  >
                    Clear
                  </Button>
                  <Button
                    sx={{ mt: { xs: 2, md: 0 } }}
                    id="btn-onSubmit"
                    variant="outlined"
                    type="submit"
                    disabled={isDisabled}
                    className={isDisabled && 'filter-btn'}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                  >
                    Filter
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        )}
        <Divider />
        <TableContainer
          className="tableContainer"
          style={{ height: data?.length === 0 || !data ? '300px' : 'auto' }}
        >
          <Table
            style={{ borderCollapse: 'separate', position: 'relative' }}
            size="small"
          >
            <TableHead>
              <CustomTableRow style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableHeaderCell content="Client" smallFont />
                <TableHeaderCell content="Project" smallFont />
                <TableHeaderCell content="Resource Name" smallFont />
                <TableHeaderCell content="Activity Type" smallFont />
                <TableHeaderCell content="Activity Id" smallFont />
                <TableHeaderCell content="Reference Id" smallFont />
                <TableHeaderCell content="Task Id" smallFont />
                <TableHeaderCell content="Task Name" smallFont />
                <TableHeaderCell content="Task Status" smallFont />
                <TableHeaderCell
                  content="Planned Duration (hh:mm)"
                  smallFont
                  isNotCapitalize
                />
                <TableHeaderCell
                  content="Duration (hh:mm)"
                  smallFont
                  isNotCapitalize
                />
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {data &&
                data?.length > 0 &&
                data.map((report, i) => {
                  return (
                    <TableRow hover key={i}>
                      <CustomTableCloumnCell content={report.clientName} />
                      <CustomTableCloumnCell content={report.projectName} />
                      <CustomTableCloumnCell content={report.employeeName} />
                      <CustomTableCloumnCell content={report.taskType} />
                      <CustomTableCloumnCell content={report.taskId} />
                      <CustomTableCloumnCell
                        content={
                          report?.referenceId ? report?.referenceId : '-'
                        }
                      />
                      <CustomTableCloumnCell
                        content={
                          report?.taskAssignId ? report.taskAssignId : '-'
                        }
                      />
                      <CustomTableCloumnCell content={report.taskName} />
                      <CustomTableCloumnCell
                        content={generateStatus(report.assignmentStatus)}
                      />
                      <CustomTableCloumnCell
                        content={report.planHours ? report.planHours : '-'}
                      />
                      <CustomTableCloumnCell content={report.hours} />
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          {(data?.length === 0 || !data) && <NoRecord />}
        </TableContainer>
        <Box
          p={1}
          ref={pagination_wrapper_ref}
          sx={{ borderTop: '1px solid #eee' }}
        >
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={total ? total : 0}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={filter.offset - 1}
            rowsPerPage={filter.limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </div>
  );
});

export default TaskBasesReportTable;
